import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { UserNotificationForm } from '@tr-common';
import { ContactPopupComponent } from '../dialogs/contact-popup/contact-popup.component';

@Component({
  selector: 'lib-notification-settings',
  template: `
    <ng-container [formGroup]="formGroup">
      <div class="mbs-20 setting-card" [class.admin-mode]="adminMode">
        <div class="flex align-center justify-between header" [class.underline]="openedReminders">
          <div>
            <h5>Reminders</h5>
            <span class="grey-label">{{adminMode ? "Notifications to remind participants about study and research opportunities." : "Notifications to remind you about study and research opportunities."}}</span>
          </div>

          <button type="button" mat-flat-button color="secondary" (click)="openedReminders = !openedReminders">
            {{ openedReminders ? 'Close' : 'Manage' }}
          </button>
        </div>

        <div *ngIf="openedReminders">
          <lib-notification-toggle class="mbs-20" formGroupName="reminders_new_ro_and_studies"
            label="New Research Opportunities &amp; Studies" [group]="true" (toggleChange)="handleToggleChange('reminders_new_ro_and_studies', $event)">
            <ng-container *ngIf="!adminMode"><span class="grey-label">Remind me about:</span></ng-container>
          </lib-notification-toggle>

          <lib-notification-toggle class="grey-filled" formGroupName="reminders_ongoing_studies" label="Ongoing Studies"
            [group]="true" [header]="false" (toggleChange)="handleToggleChange('reminders_ongoing_studies', $event)">
          </lib-notification-toggle>
        </div>
      </div>

      <div class="mbs-20 setting-card">
        <lib-notification-toggle formGroupName="newsletter"
          label="Insights to current Registry data, follow-ups from sub-studies, articles, etc." (toggleChange)="handleToggleChange('newsletter', $event)">
          <h5 class="align-center">Newsletters</h5>
        </lib-notification-toggle>
      </div>

      <div class="mbs-20 setting-card">
        <lib-notification-toggle formGroupName="management_resources" label="Resources and news for our T1D Community."  (toggleChange)="handleToggleChange('management_resources', $event)">
          <h5 class="align-center">Management Resources</h5>
        </lib-notification-toggle>
      </div>
    </ng-container>
  `,
  styleUrls: ['./notification-settings.component.scss']
})
export class NotificationSettingsComponent implements OnInit {
  @Input() adminMode = false;
  formGroup!: FormGroup<UserNotificationForm>;
  openedReminders = false;
  @Input() profileData!: any;
  @Output() openConsentPopup = new EventEmitter<{ groupName?: string, event?: { type: 'email' | 'sms', checked?: boolean } }>();  

  constructor(public controlContainer: ControlContainer,
    private dialog: MatDialog
    ) {}

  ngOnInit(): void {
    this.openedReminders = this.adminMode;
    this.formGroup = this.controlContainer.control as FormGroup<UserNotificationForm>;

    const allGroup = this.formGroup.get('all') as unknown as FormGroup;

  if (allGroup) {
    // Initial state sync on load
    const emailValue = allGroup.get('email_on')?.value;
    const smsValue = allGroup.get('sms_on')?.value;

    this.updateAllChildren('email_on', emailValue);
    this.updateAllChildren('sms_on', smsValue);

    // Live sync on toggle
    allGroup.get('email_on')?.valueChanges.subscribe((emailValue: boolean) => {
      this.updateAllChildren('email_on', emailValue);
    });

    allGroup.get('sms_on')?.valueChanges.subscribe((smsValue: boolean) => {
      this.updateAllChildren('sms_on', smsValue);
    });
  }
}

// Implemented as part of ROZ-2089: SMS toggles enhancement.
// This functionality handles admin-side toggle behavior:
// - When the admin turns ON the parent toggle, all child toggles are turned OFF and disabled.
// - When the admin turns OFF the parent toggle, all child toggles are enabled.
private updateAllChildren(type: 'email_on' | 'sms_on', allToggleValue: boolean) {
  const childKeys = [
    'reminders_new_ro_and_studies',
    'reminders_ongoing_studies',
    'newsletter',
    'management_resources'
  ];

  childKeys.forEach((key) => {
    const group = this.formGroup.get(key) as FormGroup;
    const control = group?.get(type);

    if (control) {
      if (allToggleValue) {
           // All is OFF: enable children for manual control
        control.enable({ emitEvent: false });
      } else {
          // All is ON: force OFF and disable children
          control.setValue(false, { emitEvent: false });
          control.disable({ emitEvent: false });
      }
    }
  });
}


/**
* Handle toggle change event.
* If SMS is checked and the user has not consented to SMS, open a consent popup.
* If the user has not toggled on the specific type of notification, open a contact popup.
* If in admin mode, do not apply these checks and simply let the toggle continue.
* @param groupName Name of the group that was toggled.
* @param event The toggle event.
*/
handleToggleChange(groupName: string, event: { type: 'email' | 'sms', checked: boolean }) {
  if (!event.checked) return; // Do nothing if turning OFF
  const control = this.formGroup.get(groupName)?.get(`${event.type}_on`);

  // Apply checks only if not in admin mode
  if (!this.adminMode) {
 // Step 1: Check main consent
    if (event.type === 'sms' && !this.profileData.info.is_core_consent_procedure_closed) {
      const dialogRef = this.dialog.open(ContactPopupComponent, {
        panelClass: 'custom-dialog-radius',
        width: '500px',
        hasBackdrop: true,
        data: { }
      });
      dialogRef.afterClosed().subscribe(() => {
       // Revert toggle silently **only for SMS**
       if (event.type === 'sms') {
        control?.setValue(false, { emitEvent: false });
        }
      });
      return;
    }

    // Step 2: Check consent if SMS
    if (event.type === 'sms' && !this.profileData.info.sms_consented) {
      this.openConsentPopup.emit({ groupName, event });
      return;
    }

   // Step 3: Check toggle_on for specific type
   const settingKey = `${event.type}_on` as 'email_on' | 'sms_on';
   if (!this.profileData.settings[settingKey]) {
     const dialogRef = this.dialog.open(ContactPopupComponent, {
       panelClass: 'custom-dialog-radius',
       width: '500px',
       hasBackdrop: true,
       data: { }
     });
     dialogRef.afterClosed().subscribe(() => {
       // Revert toggle silently
       control?.setValue(false, { emitEvent: false });
     });
     return;
   }
  }
}

/**
* Handles the case when the user has given consent to SMS and then wants to toggle SMS on.
* We need to reset the toggle to false so that the user is not confused when they go back to the main screen.
* @param groupName Name of the group that was toggled.
* @param event The toggle event.
*/
afterConsentAction(groupName: string, event: { type: 'email' | 'sms', checked: boolean }) {
  const control = this.formGroup.get(groupName)?.get(`${event.type}_on`);
  control?.setValue(false, { emitEvent: false });
}
}
